import * as React from 'react';
import { useMsal } from "@azure/msal-react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Label, Link, Switch, SwitchOnChangeData, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from "@fluentui/react-components";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { dataActionCreators, Invoice } from "../../store/Data";
import { TeamsState } from "../../store/TeamsState";
import { TokenHelper } from "../../TokenHelper";
import { useEditorStyles, useStackClassName } from "../Styles/Editor";
import { useCustomers } from '../Tools/ObjectLoader';

export const DownloadGenInvoiceFile = async (InvoiceId: string, token: string) => {
    const params: AxiosRequestConfig = {
        timeout: 20000, // 20 seconds
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        responseType: 'blob',
    };

    const instance: AxiosInstance = axios.create(params);
    try {
        /*const res: AxiosResponse =*/ await instance.get('api/Invoices/' + InvoiceId + "/GenInvoice");
        //let filename = "Neomytic";
        //const disposition = res.headers['content-disposition'];
        //if (disposition && disposition.indexOf('attachment') !== -1) {
        //    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        //    const matches = filenameRegex.exec(disposition);
        //    if (matches != null && matches[1]) {
        //        filename = matches[1].replace(/['"]/g, '');
        //    }
        //}
        //saveAs(res.data as Blob, filename);
    }
    catch (res) {
        if (axios.isAxiosError(res)) {
            const res2 = res as AxiosError;
            if (res2.response && res2.response.status === 409) {
                const blob = res2.response.data as Blob;
                if (blob.type && blob.type === "text/plain") {
                    throw new Error(await blob.text());
                }
            }
        }
        throw new Error("error");
    }
};

export const InvoicesGeneration = () => {
    // All data needed for edition should be loaded before calling the editor
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const invoiceGeneration = useSelector<ApplicationState, Invoice | null>((state: ApplicationState) => state.data.invoiceGeneration);
    const { customers } = useCustomers();
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const customer = React.useMemo(() => {
        return customers && invoiceGeneration ? customers.find((c) => invoiceGeneration && c.customerid === invoiceGeneration.customerid) : undefined;
    }, [customers, invoiceGeneration]);

    const [confirmOverwrive, setConfirmOverwrive] = React.useState<boolean>(false);

    if (invoiceGeneration === null) {
        return null;
    }

    const submit = () => {
        const toastId = (Math.random() + 1).toString(36).substring(7);
        dispatchToast(
            <Toast appearance="inverted">
                <ToastTitle>
                    Generation ...
                </ToastTitle>
            </Toast>,
            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
        );
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null && invoiceGeneration.invoiceid) {
                    DownloadGenInvoiceFile(invoiceGeneration.invoiceid, token)
                        .then(() => {
                            updateToast({
                                content: (
                                    <Toast appearance="inverted">
                                        <ToastTitle action={
                                            <ToastTrigger>
                                                <Link>Dismiss</Link>
                                            </ToastTrigger>
                                        }>
                                            Generation succeed
                                        </ToastTitle>
                                    </Toast>
                                ),
                                intent: "success",
                                toastId,
                                timeout: 5000,
                            });
                            fetch("api/Invoices/" + invoiceGeneration.invoiceid, {
                                method: "GET",
                                headers: {
                                    "Content-Type": "application/json",
                                    authorization: "bearer " + token,
                                },
                                mode: "cors",
                                cache: "default",
                            })
                                .then(response => {
                                    if (response.status === 200) {
                                        return response.json() as Promise<Invoice>;
                                    }
                                    else {
                                        updateToast({
                                            content: (
                                                <Toast appearance="inverted">
                                                    <ToastTitle action={
                                                        <ToastTrigger>
                                                            <Link>Dismiss</Link>
                                                        </ToastTrigger>
                                                    }>
                                                        Retrieving failed
                                                    </ToastTitle>
                                                </Toast>
                                            ),
                                            intent: "error",
                                            toastId,
                                            timeout: 10000,
                                        });
                                    }
                                })
                                .then(data => {
                                    if (data) {
                                        dispatch(dataActionCreators.patchInvoiceEdition(data));
                                        dispatch(dataActionCreators.requestInvoiceGeneration(null));
                                    }
                                });

                        })
                        .catch((e) => {
                            updateToast({
                                content: (
                                    <Toast appearance="inverted">
                                        <ToastTitle action={
                                            <ToastTrigger>
                                                <Link>Dismiss</Link>
                                            </ToastTrigger>
                                        }>
                                            Generation failed
                                        </ToastTitle>
                                        <ToastBody>{e.message}</ToastBody>
                                    </Toast>
                                ),
                                intent: "error",
                                toastId,
                                timeout: 10000,
                            });
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{"Invoice Generation"}</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestInvoiceGeneration(null));
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        <Field label={t('InvoiceRef')}>
                            <Label className={styles.label}>{invoiceGeneration.invoiceRef ? invoiceGeneration.invoiceRef : "-"}</Label>
                        </Field>
                        <Field label={t('Customer')} className={styles.fluid}>
                            <Label className={styles.label}>{customer && customer.displayName ? customer.displayName : "-"}</Label>
                        </Field>
                        <Field label="Date" className={styles.fluid}>
                            <Label className={styles.label}>{invoiceGeneration.invoiceDate ? new Date(invoiceGeneration.invoiceDate).toLocaleDateString() : "-"}</Label>
                        </Field>
                        <Switch label={t('ConfirmInvoiceGenOverWrite')}
                            checked={confirmOverwrive}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setConfirmOverwrive(data.checked); }} />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestInvoiceGeneration(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        disabled={invoiceGeneration.invoiceSharePointFileUniqueId !== null ? !confirmOverwrive : false}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};